import { useState } from "react";
import type { ResultVariableKey } from "../model/results/resultsVariables.ts";

const DEFAULT_NODE_VARIABLE: ResultVariableKey = "depth";
const DEFAULT_LINK_VARIABLE: ResultVariableKey = "flow";
const DEFAULT_SUBCATCHMENT_VARIABLE: ResultVariableKey = "runoff";

export function useResultsAnimation() {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  // Subcatchment fill defaults ON along with nodes/links, but its 45% opacity
  // over a large polygon can wash out everything underneath, so each
  // category can be toggled off independently.
  const [showNodes, setShowNodes] = useState(true);
  const [showLinks, setShowLinks] = useState(true);
  const [showSubcatchments, setShowSubcatchments] = useState(true);
  const [nodeVariable, setNodeVariable] = useState<ResultVariableKey>(DEFAULT_NODE_VARIABLE);
  const [linkVariable, setLinkVariable] = useState<ResultVariableKey>(DEFAULT_LINK_VARIABLE);
  const [subcatchmentVariable, setSubcatchmentVariable] = useState<ResultVariableKey>(
    DEFAULT_SUBCATCHMENT_VARIABLE
  );

  // Resets on every (re)open ONLY — a stale index from a longer previous run
  // could be out of bounds for a shorter one. Closing must NOT reset: it used
  // to run unconditionally on every toggle, silently discarding whatever the
  // user had just customized (shown categories, variables, scrub position)
  // the instant they closed the panel, rather than only on the next re-open.
  function toggle() {
    const next = !open;
    if (next) {
      setIndex(0);
      setPlaying(false);
      setShowNodes(true);
      setShowLinks(true);
      setShowSubcatchments(true);
      setNodeVariable(DEFAULT_NODE_VARIABLE);
      setLinkVariable(DEFAULT_LINK_VARIABLE);
      setSubcatchmentVariable(DEFAULT_SUBCATCHMENT_VARIABLE);
    }
    setOpen(next);
  }

  function close() {
    setOpen(false);
  }

  return {
    open,
    index,
    playing,
    setIndex,
    setPlaying,
    toggle,
    close,
    showNodes,
    setShowNodes,
    showLinks,
    setShowLinks,
    showSubcatchments,
    setShowSubcatchments,
    nodeVariable,
    setNodeVariable,
    linkVariable,
    setLinkVariable,
    subcatchmentVariable,
    setSubcatchmentVariable,
  };
}
